"use client";

import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import type { StoryblokStory } from "@/types/redirects";

const StorySearch = ({
  hideSearchBar,
  updateRedirect,
}: {
  hideSearchBar: () => void;
  updateRedirect?: (val: string, uuid: string) => void;
}) => {
  const searchRef = useRef<HTMLDivElement>(null);
  const [stories, setStories] = useState<StoryblokStory[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const { ref, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") hideSearchBar();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hideSearchBar]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        !searchRef.current?.contains(e.target as Node) &&
        !searchRef.current?.parentElement?.contains(e.target as Node)
      ) {
        hideSearchBar();
      }
    };
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [hideSearchBar]);

  useEffect(() => {
    const q = search.trim();
    if (!q) {
      setStories([]);
      setHasMore(false);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(
        `/api/redirects?search=${encodeURIComponent(q)}&page=${page}`,
        { signal: controller.signal },
      )
        .then((data) => data.json())
        .then((items: StoryblokStory[]) => {
          const list = Array.isArray(items) ? items : [];
          setHasMore(list.length > 0);
          if (page > 1) {
            setStories((prev) => [...prev, ...list]);
          } else {
            setStories(list);
          }
        })
        .catch((err) => {
          if (err?.name !== "AbortError") console.error(err);
        });
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [page, search]);

  useEffect(() => {
    if (inView && hasMore) setPage((prev) => prev + 1);
  }, [inView, hasMore]);

  return (
    <div
      ref={searchRef}
      className="absolute bg-white rounded-lg shadow-lg p-4 w-[560px] font-sans z-10 top-10 left-[45px]"
    >
      <div className="flex items-center border border-gray-300 rounded-md px-3 py-2 mb-4">
        <svg
          className="w-4 h-4 text-gray-400 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-4.35-4.35M17 10.5A6.5 6.5 0 104.5 17 6.5 6.5 0 0017 10.5z"
          />
        </svg>
        <input
          type="text"
          autoFocus
          placeholder="Search for content items"
          className="flex-1 border-none outline-none text-base bg-transparent"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
            setHasMore(true);
          }}
        />
      </div>

      <ul className="list-none mt-4 p-0 max-h-[300px] overflow-y-auto">
        {stories.map((item) => (
          <li
            key={item.uuid}
            onClick={() => {
              if (updateRedirect) {
                updateRedirect(item.full_slug, item.uuid);
              }
              hideSearchBar();
            }}
            className="p-3 border-b border-gray-200 flex flex-col cursor-pointer hover:bg-gray-50"
          >
            <span
              className={`font-${item.published_at ? "semibold text-green-600" : "normal"}`}
            >
              {item.name}
            </span>
            <span className="mt-1 text-sm text-gray-500">{item.full_slug}</span>
            <span className="text-sm text-gray-500">
              {item.published_at ? "Published" : "Draft"}
            </span>
          </li>
        ))}
        <div ref={ref} style={{ height: 1 }} />
      </ul>
    </div>
  );
};

export default StorySearch;
